import React, { useEffect, useState, useMemo } from 'react';
import { Button, Input, Select, MultiSelect, Modal, PositionForm, BenefitForm } from '..';
import {
  FiUser,
  FiMail,
  FiSmartphone,
  FiHash,
  FiClock,
  FiMapPin,
  FiFileText,
  FiFlag,
  FiHome,
  FiUserCheck,
  FiCreditCard,
  FiCalendar,
  FiDollarSign,
  FiZap,
  FiType,
  FiKey,
  FiList,
  FiRepeat,
  FiTag,
  FiLayers,
  FiTarget,
  FiInfo,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../Api';
import '../CompanyForm/style.css';
import Helpers from '../../Helpers';
import { BANK_OPTIONS } from '../../constants/banks';

export default function EmployeeForm({ companyId, initialData, onSuccess, onCancel, companies = [] }) {
  const isEdit = !!initialData;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [selectedCompanyId, setSelectedCompanyId] = useState(
    initialData?.company?.id || initialData?.company_id 
      ? String(initialData?.company?.id || initialData?.company_id) 
      : (companyId ? String(companyId) : '')
  );

  const [stations, setStations] = useState([]);
  const [journeys, setJourneys] = useState([]);
  const [positions, setPositions] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [selectedBenefitIds, setSelectedBenefitIds] = useState([]);

  // Recurring Expense State
  const [createRecurringExpense, setCreateRecurringExpense] = useState('false');
  const [expenseType, setExpenseType] = useState('salary'); // 'salary' or 'salary_benefits'
  const [recurrenceData, setRecurrenceData] = useState({
    repetition: 'MENSAL',
    duration: '12', // 12 months = 1 year
    charge_on: '5',
    first_due_date: '',
    payment_method: '', // Account ID
    category_id: '',
    cost_center_id: '',
  });

  const [categories, setCategories] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [isBenefitModalOpen, setIsBenefitModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    secondary_phone: '',
    sex: '',
    type: 'C',
    status: 'Ativo',
    is_active: 'true',
    admission_date: '',
    registration_number: '',
    lotacao: '',
    homeoffice: 'false',
    work_start_time: '',
    last_vacation_period: '',
    birth_date: '',
    cpf: '',
    rg: '',
    rg_issuer: '',
    rg_issue_date: '',
    ctps_number: '',
    ctps_series: '',
    pis_pasep: '',
    father_name: '',
    mother_name: '',
    height: '',
    weight: '',
    is_pcd: 'false',
    disability_type: '',
    bank_name: '',
    bank_agency: '',
    bank_account: '',
    bank_account_type: '',
    has_pix: 'false',
    pix_key_type: '',
    pix_key: '',
    address_street: '',
    address_number: '',
    address_complement: '',
    address_district: '',
    address_city: '',
    address_state: '',
    address_zip_code: '',
    service_station_ids: [],
    journey_id: '',
    position_id: '',
    salary: '',
    has_family_benefit: 'false',
    dependents_count: '',
    allow_round: 'false'
  });

  useEffect(() => {
    async function loadDependencies() {
      if (!selectedCompanyId) return;

      try {
        const [stationsData, journeysData, positionsData, benefitsData, categoriesData, costCentersData, accountsData] = await Promise.all([
          Api.getServiceStations(selectedCompanyId),
          Api.getJourneys(selectedCompanyId),
          Api.getPositions(selectedCompanyId),
          Api.getBenefits(selectedCompanyId),
          Api.getCategories(selectedCompanyId),
          Api.getCostCenters(selectedCompanyId),
          Api.getAccounts(selectedCompanyId),
        ]);

        if (Array.isArray(stationsData)) {
          setStations(stationsData);
        }
        if (Array.isArray(journeysData)) {
          setJourneys(journeysData);
        }
        if (Array.isArray(positionsData)) {
          setPositions(positionsData);
        }
        if (Array.isArray(benefitsData)) {
          setBenefits(benefitsData);
        }
        if (Array.isArray(categoriesData)) {
          setCategories(categoriesData);
        }
        if (Array.isArray(costCentersData)) {
          setCostCenters(costCentersData);
        }
        if (Array.isArray(accountsData)) {
          setAccounts(accountsData);
        }
      } catch (err) {
        toast.error('Erro ao carregar dados auxiliares.');
      }
    }

    loadDependencies();
  }, [selectedCompanyId]);

  useEffect(() => {
    if (initialData) {
      const dataCompanyId = initialData.company?.id || initialData.company_id;
      if (dataCompanyId) {
        setSelectedCompanyId(String(dataCompanyId));
      } else if (companyId) {
        setSelectedCompanyId(String(companyId));
      }

      setFormData({
        name: initialData.name || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        secondary_phone: initialData.secondary_phone || '',
        sex: initialData.sex || '',
        type: initialData.type || 'C',
        status: initialData.status || 'Ativo',
        is_active:
          initialData.is_active === false
            ? 'false'
            : 'true',
        admission_date: initialData.admission_date || '',
        registration_number: initialData.registration_number || '',
        lotacao: initialData.lotacao || '',
        homeoffice:
          initialData.homeoffice === true
            ? 'true'
            : 'false',
        allow_round:
          initialData.allow_round === true
            ? 'true'
            : 'false',
        work_start_time: initialData.work_start_time || '',
        last_vacation_period: initialData.last_vacation_period || '',
        birth_date: initialData.birth_date || '',
        cpf: initialData.cpf || '',
        rg: initialData.rg || '',
        rg_issuer: initialData.rg_issuer || '',
        rg_issue_date: initialData.rg_issue_date || '',
        ctps_number: initialData.ctps_number || '',
        ctps_series: initialData.ctps_series || '',
        pis_pasep: initialData.pis_pasep || '',
        father_name: initialData.father_name || '',
        mother_name: initialData.mother_name || '',
        height:
          initialData.height != null ? String(initialData.height) : '',
        weight:
          initialData.weight != null ? String(initialData.weight) : '',
        is_pcd:
          initialData.is_pcd === true
            ? 'true'
            : 'false',
        disability_type: initialData.disability_type || '',
        bank_name: initialData.bank_name || '',
        bank_agency: initialData.bank_agency || '',
        bank_account: initialData.bank_account || '',
        bank_account_type: initialData.bank_account_type || '',
        has_pix:
          initialData.has_pix === true
            ? 'true'
            : 'false',
        pix_key_type: initialData.pix_key_type || '',
        pix_key: initialData.pix_key || '',
        address_street: initialData.address_street || '',
        address_number: initialData.address_number || '',
        address_complement: initialData.address_complement || '',
        address_district: initialData.address_district || '',
        address_city: initialData.address_city || '',
        address_state: initialData.address_state || '',
        address_zip_code: initialData.address_zip_code || '',
        service_station_ids:
          initialData.serviceStations
            ? initialData.serviceStations.map(s => String(s.id))
            : initialData.service_station_id
              ? [String(initialData.service_station_id)]
              : [],
        journey_id:
          initialData.journey_id != null
            ? String(initialData.journey_id)
            : '',
        position_id:
          initialData.position_id != null
            ? String(initialData.position_id)
            : '',
        salary:
          initialData.salary != null ? String(initialData.salary) : '',
        has_family_benefit:
          initialData.has_family_benefit === true ? 'true' : 'false',
        dependents_count:
          initialData.dependents_count != null ? String(initialData.dependents_count) : '',
      });

      if (Array.isArray(initialData.benefits)) {
        setSelectedBenefitIds(
          initialData.benefits
            .map(benefit => (benefit && benefit.id != null ? String(benefit.id) : null))
            .filter(Boolean)
        );
      }
    }
  }, [initialData]);

  function handleChange(event) {
    const { id, value } = event.target;
    setFormData(prev => {
      if (id === 'status') {
        const isActive = value !== 'Inativo';
        return { ...prev, status: value, is_active: isActive ? 'true' : 'false' };
      }
      if (id === 'is_pcd' && (value === 'false' || value === false)) {
        return { ...prev, is_pcd: value, disability_type: '' };
      }
      if (id === 'has_family_benefit' && (value === 'false' || value === false)) {
        return { ...prev, has_family_benefit: value, dependents_count: '' };
      }
      if (id === 'pix_key_type') {
        return { ...prev, pix_key_type: value, pix_key: '' };
      }
      if (id === 'journey_id') {
        const selectedJourney = journeys.find(j => String(j.id) === String(value));
        return {
          ...prev,
          journey_id: value,
          work_start_time: selectedJourney ? selectedJourney.start_time : prev.work_start_time,
        };
      }
      return { ...prev, [id]: value };
    });
  }

  function handleRecurrenceChange(event) {
    const { id, value } = event.target;
    setRecurrenceData(prev => ({ ...prev, [id]: value }));
  }

  function validateForm() {
    const messages = [];

    if (!formData.name || !formData.name.trim()) {
      messages.push('Informe o nome do funcionário.');
    }

    if (!formData.type) {
      messages.push('Informe o tipo do funcionário.');
    }

    if (!isEdit && createRecurringExpense === 'true') {
      if (!recurrenceData.repetition) messages.push('Selecione a repetição da despesa.');
      if (!recurrenceData.duration) messages.push('Selecione a duração da despesa.');
      if (!recurrenceData.charge_on) messages.push('Selecione o dia de cobrança.');
      if (!recurrenceData.first_due_date) messages.push('Informe o primeiro vencimento.');
      if (!recurrenceData.category_id) messages.push('Selecione a categoria da despesa.');
      if (!recurrenceData.cost_center_id) messages.push('Selecione o centro de custo da despesa.');
    }

    if (messages.length > 0) {
      messages.forEach(message => toast.error(message));
      return false;
    }

    return true;
  }

  function parseNumber(value) {
    if (value === undefined || value === null) {
      return null;
    }
    const trimmed = String(value).trim();
    if (!trimmed) {
      return null;
    }
    let normalized = trimmed;

    if (normalized.includes('R$') || (normalized.includes('.') && normalized.includes(','))) {
      normalized = normalized
        .replace(/R\$\s?/g, '')
        .replace(/\./g, '')
        .replace(',', '.');
    } else {
      normalized = normalized.replace(',', '.');
    }

    const num = Number(normalized);
    if (Number.isNaN(num)) {
      return null;
    }
    return num;
  }

  function parseBoolean(value) {
    if (value === 'true' || value === true) {
      return true;
    }
    if (value === 'false' || value === false) {
      return false;
    }
    return null;
  }

  function parseId(value) {
    if (value === undefined || value === null) {
      return null;
    }
    const trimmed = String(value).trim();
    if (!trimmed) {
      return null;
    }
    const num = Number(trimmed);
    if (Number.isNaN(num)) {
      return null;
    }
    return num;
  }

  function getPixKeyMask(pixKeyType) {
    if (!pixKeyType) {
      return null;
    }
    if (pixKeyType === 'cpf') {
      return 'cpf';
    }
    if (pixKeyType === 'celular') {
      return 'phone';
    }
    if (pixKeyType === 'cnpj') {
      return 'cnpj';
    }
    return null;
  }

  async function handleCepBlur(event) {
    const value = event.target.value || '';
    const digits = value.replace(/\D/g, '');

    if (digits.length !== 8) {
      return;
    }

    try {
      const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await response.json();

      if (data && data.erro) {
        toast.error('CEP não encontrado.');
        return;
      }

      setFormData(prev => ({
        ...prev,
        address_street: data.logradouro || prev.address_street,
        address_district: data.bairro || prev.address_district,
        address_city: data.localidade || prev.address_city,
        address_state: data.uf || prev.address_state,
      }));
    } catch (err) {
      toast.error('Erro ao buscar CEP.');
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!selectedCompanyId) {
      toast.error('Selecione uma empresa para cadastrar funcionários.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const payload = {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        secondary_phone: formData.secondary_phone || null,
        sex: formData.sex || null,
        type: formData.type,
        status: formData.status || null,
        is_active: formData.is_active,
        admission_date: formData.admission_date || null,
        registration_number: formData.registration_number || null,
        lotacao: formData.lotacao || null,
        homeoffice: formData.homeoffice,
        allow_round: parseBoolean(formData.allow_round),
        has_family_benefit: formData.has_family_benefit,
        dependents_count: parseNumber(formData.dependents_count),
        work_start_time: formData.work_start_time || null,
        last_vacation_period: formData.last_vacation_period || null,
        birth_date: formData.birth_date || null,
        cpf: formData.cpf || null,
        rg: formData.rg || null,
        rg_issuer: formData.rg_issuer || null,
        rg_issue_date: formData.rg_issue_date || null,
        ctps_number: formData.ctps_number || null,
        ctps_series: formData.ctps_series || null,
        pis_pasep: formData.pis_pasep || null,
        father_name: formData.father_name || null,
        mother_name: formData.mother_name || null,
        height: parseNumber(formData.height),
        weight: parseNumber(formData.weight),
        salary: parseNumber(formData.salary),
        is_pcd: formData.is_pcd,
        disability_type: formData.disability_type || null,
        bank_name: formData.bank_name || null,
        bank_agency: formData.bank_agency || null,
        bank_account: formData.bank_account || null,
        bank_account_type: formData.bank_account_type || null,
        has_pix: formData.has_pix,
        pix_key_type: formData.pix_key_type || null,
        pix_key: formData.pix_key || null,
        address_street: formData.address_street || null,
        address_number: formData.address_number || null,
        address_complement: formData.address_complement || null,
        address_district: formData.address_district || null,
        address_city: formData.address_city || null,
        address_state: formData.address_state || null,
        address_zip_code: formData.address_zip_code || null,
        service_station_ids: formData.service_station_ids
          .map(id => parseId(id))
          .filter(value => value !== null),
        journey_id: parseId(formData.journey_id),
        position_id: parseId(formData.position_id),
        benefit_ids: selectedBenefitIds
          .map(id => parseId(id))
          .filter(value => value !== null),
        recurring_expense: createRecurringExpense === 'true' ? {
          enabled: true,
          type: expenseType, // 'salary' or 'salary_benefits'
          repetition: recurrenceData.repetition,
          duration: recurrenceData.duration,
          charge_on: recurrenceData.charge_on,
          first_due_date: recurrenceData.first_due_date,
          payment_method: recurrenceData.payment_method || null,
          category_id: parseId(recurrenceData.category_id),
          cost_center_id: parseId(recurrenceData.cost_center_id),
        } : null,
      };

      let result;

      if (isEdit && initialData && initialData.id) {
        result = await Api.updateEmployee(selectedCompanyId, initialData.id, payload);
      } else {
        result = await Api.createEmployee(selectedCompanyId, payload);
      }

      if (result && !result.error) {
        if (onSuccess) {
          onSuccess(result);
        }
      } else {
        const message = result && result.error ? result.error : 'Erro ao salvar funcionário.';
        setError(message);
        toast.error(message);
      }
    } catch (err) {
      setError('Erro ao salvar funcionário. Tente novamente.');
      toast.error('Erro ao salvar funcionário. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  const companyOptions = useMemo(() => {
    return [
      { value: '', label: 'SELECIONE...' },
      ...companies.map(c => ({
        value: String(c.id),
        label: c.corporate_name || c.trade_name || c.name || c.razao_social || c.nome_fantasia || `Empresa ${c.id}`
      }))
    ];
  }, [companies]);

  const stationOptions = [
    { value: '', label: 'SELECIONE...' },
    ...stations.map(station => ({
      value: String(station.id),
      label: station.nickname || station.name,
    })),
  ];

  const journeyOptions = [
    { value: '', label: 'SELECIONE...' },
    ...journeys.map(journey => ({
      value: String(journey.id),
      label: journey.name,
    })),
  ];

  const positionOptions = useMemo(() => ([
    { value: '', label: 'SELECIONE...' },
    ...positions.map(position => ({
      value: String(position.id),
      label: position.name,
    })),
  ]), [positions]);

  const benefitOptions = useMemo(() => benefits.map(benefit => {
    const valueLabel =
      benefit.value != null
        ? Helpers.toBRL(benefit.value)
        : null;

    let frequencyLabel = '';
    if (benefit.frequency === 'daily') {
      frequencyLabel = 'DIÁRIO';
    } else if (benefit.frequency === 'monthly') {
      frequencyLabel = 'MENSAL';
    }

    const parts = [benefit.name];
    if (valueLabel) {
      if (frequencyLabel) {
        parts.push(`- ${valueLabel} (${frequencyLabel})`);
      } else {
        parts.push(`- ${valueLabel}`);
      }
    }

    return {
      value: String(benefit.id),
      label: parts.join(' '),
    };
  }), [benefits]);

  const disabilityTypeOptions = [
    { value: '', label: 'SELECIONE...' },
    { value: 'Física', label: 'Física' },
    { value: 'Visual', label: 'Visual' },
    { value: 'Auditiva', label: 'Auditiva' },
    { value: 'Intelectual', label: 'Intelectual' },
    { value: 'Múltipla', label: 'Múltipla' },
    { value: 'Psicossocial', label: 'Psicossocial / Transtorno mental' },
    { value: 'Reabilitado', label: 'Reabilitado' },
    { value: 'Outras', label: 'Outras' },
  ];

  const bankOptions = [
    { value: '', label: 'Selecione o banco...' },
    ...BANK_OPTIONS
  ];

  const categoryOptions = [
    { value: '', label: 'SELECIONE...' },
    ...categories.map(c => ({ value: String(c.id), label: c.name }))
  ];

  const costCenterOptions = [
    { value: '', label: 'SELECIONE...' },
    ...costCenters.map(cc => ({ value: String(cc.id), label: cc.name }))
  ];

  const paymentMethodOptions = [
    { value: '', label: 'SELECIONE...' },
    { value: 'BOLETO', label: 'Boleto' },
    { value: 'PIX', label: 'PIX' },
    { value: 'CREDIT_CARD', label: 'Cartão de Crédito' },
    { value: 'DEBIT_CARD', label: 'Cartão de Débito' },
    { value: 'TRANSFER', label: 'Transferência' },
    { value: 'CASH', label: 'Dinheiro' },
  ];

  return (
    <>
    <form onSubmit={handleSubmit} className="company-form-component">
      <div className="form-body">
        <h3 className="section-title" style={{ marginTop: '0px' }}>Empresa</h3>
        <div className="form-grid">
          <div className="full-width">
            <Select
              id="company_id"
              label="Empresa *"
              value={selectedCompanyId}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
              options={companyOptions}
              icon={FiHome}
              searchable
            />
          </div>
        </div>

        <h3 className="section-title">Dados do funcionário</h3>
        <div className="form-grid">
          <div className="full-width">
            <Input
              id="name"
              label="Nome *"
              value={formData.name}
              onChange={handleChange}
              placeholder="Nome completo"
              required
              icon={FiUser}
            />
          </div>
          <Input
            id="email"
            label="E-mail"
            value={formData.email}
            onChange={handleChange}
            placeholder="email@exemplo.com"
            icon={FiMail}
          />
          <Input
            id="phone"
            label="Whatsapp"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(99) 99999-9999"
            mask="phone"
            icon={FiSmartphone}
          />
          <Input
            id="secondary_phone"
            label="Celular Secundário"
            value={formData.secondary_phone}
            onChange={handleChange}
            placeholder="(99) 99999-9999"
            mask="phone"
            icon={FiSmartphone}
          />
          <Select
            id="type"
            label="Tipo *"
            value={formData.type}
            onChange={handleChange}
            options={[
              { value: 'C', label: 'Colaborador' },
              { value: 'F', label: 'Freelancer' },
              { value: 'O', label: 'Cooperado' },
            ]}
            icon={FiFileText}
            searchable
          />
          <Select
            id="status"
            label="Status"
            value={formData.status}
            onChange={handleChange}
            options={[
              { value: 'Ativo', label: 'Ativo', color: 'var(--success-color)' },
              { value: 'Inativo', label: 'Inativo', color: 'var(--error-color)' },
              { value: 'Atestado Médico', label: 'Atestado Médico', color: 'var(--info-color)' },
              { value: 'Afastado INSS', label: 'Afastado INSS', color: 'var(--warning-color)' },
              { value: 'Férias', label: 'Férias', color: 'var(--warning-color)' },
            ]}
            icon={FiUserCheck}
            searchable
          />
        </div>

        <h3 className="section-title">Dados profissionais</h3>
        <div className="form-grid">
          <Input
            id="admission_date"
            label="Data de admissão"
            type="date"
            value={formData.admission_date}
            onChange={handleChange}
            icon={FiCalendar}
          />
          <Input
            id="registration_number"
            label="Matrícula"
            value={formData.registration_number}
            type="number"
            inputMode="numeric"
            onChange={handleChange}
            icon={FiHash}
          />
          <Select
            id="has_family_benefit"
            label="Benefício Família"
            value={formData.has_family_benefit}
            onChange={handleChange}
            options={[
              { value: 'false', label: 'Não' },
              { value: 'true', label: 'Sim' },
            ]}
            icon={FiFileText}
            searchable
          />
          {formData.has_family_benefit === 'true' && (
            <Input
              id="dependents_count"
              label="Número de Dependentes"
              type="number"
              inputMode="numeric"
              value={formData.dependents_count}
              onChange={handleChange}
              icon={FiHash}
            />
          )}
          <Select
            id="homeoffice"
            label="Home office"
            value={formData.homeoffice}
            onChange={handleChange}
            options={[
              { value: 'false', label: 'Não' },
              { value: 'true', label: 'Sim' },
            ]}
            icon={FiHome}
            searchable
          />
          <div style={{ display: 'none' }}>
            <Input
              id="work_start_time"
              label="Horário de trabalho"
              type="time"
              value={formData.work_start_time}
              onChange={handleChange}
              icon={FiClock}
            />
          </div>
          <Input
            id="last_vacation_period"
            label="Último período de férias"
            type="date"
            value={formData.last_vacation_period}
            onChange={handleChange}
            icon={FiCalendar}
          />
          <MultiSelect
            id="service_station_ids"
            label="Postos de Serviço"
            value={formData.service_station_ids}
            onChange={val => setFormData({ ...formData, service_station_ids: val })}
            options={stations.map(station => ({
              value: String(station.id),
              label: station.nickname || station.name,
            }))}
            icon={FiMapPin}
            placeholder="Selecione os postos..."
          />
          <Select
            id="journey_id"
            label="Jornada"
            value={formData.journey_id}
            onChange={handleChange}
            options={journeyOptions}
            icon={FiClock}
            searchable
          />
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <Select
                id="position_id"
                label="Cargo"
                value={formData.position_id}
                onChange={handleChange}
                options={positionOptions}
                icon={FiUserCheck}
                searchable
              />
            </div>
            <Button
              type="button"
              onClick={() => setIsPositionModalOpen(true)}
              style={{ height: '46px', padding: '0 0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              +
            </Button>
          </div>
          <Input
            id="salary"
            label="Salário"
            value={formData.salary}
            onChange={handleChange}
            placeholder="0,00"
            icon={FiDollarSign}
            mask="currency"
          />
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <MultiSelect
                id="benefit_ids"
                label="Benefícios"
                value={selectedBenefitIds}
                onChange={setSelectedBenefitIds}
                options={benefitOptions}
                icon={FiFileText}
                placeholder="Selecione os benefícios..."
              />
            </div>
            <Button
              type="button"
              onClick={() => setIsBenefitModalOpen(true)}
              style={{ height: '46px', padding: '0 0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              +
            </Button>
          </div>
        </div>

        <h3 className="section-title">Dados pessoais</h3>
        <div className="form-grid">
          <Input
            id="birth_date"
            label="Data de nascimento"
            type="date"
            value={formData.birth_date}
            onChange={handleChange}
            icon={FiClock}
          />
          <Select
            id="sex"
            label="Sexo"
            value={formData.sex}
            onChange={handleChange}
            options={[
              { value: '', label: 'SELECIONE...' },
              { value: 'male', label: 'Masculino' },
              { value: 'female', label: 'Feminino' },
              { value: 'other', label: 'Outro' },
            ]}
            icon={FiUser}
            searchable
          />
          <Input
            id="cpf"
            label="CPF"
            value={formData.cpf}
            onChange={handleChange}
            mask="cpf"
            icon={FiHash}
          />
          <Input
            id="rg"
            label="RG"
            value={formData.rg}
            onChange={handleChange}
            icon={FiHash}
          />
          <Input
            id="rg_issuer"
            label="Órgão emissor"
            value={formData.rg_issuer}
            onChange={handleChange}
            icon={FiFileText}
          />
          <Input
            id="rg_issue_date"
            label="Data emissão RG"
            type="date"
            value={formData.rg_issue_date}
            onChange={handleChange}
            icon={FiClock}
          />
          <Input
            id="ctps_number"
            label="Número CTPS"
            value={formData.ctps_number}
            onChange={handleChange}
            icon={FiHash}
          />
          <Input
            id="ctps_series"
            label="Série CTPS"
            value={formData.ctps_series}
            onChange={handleChange}
            icon={FiHash}
          />
          <Input
            id="pis_pasep"
            label="PIS/PASEP"
            value={formData.pis_pasep}
            onChange={handleChange}
            icon={FiHash}
          />
          <div className="full-width">
            <Input
              id="father_name"
              label="Nome do pai"
              value={formData.father_name}
              onChange={handleChange}
              icon={FiUser}
            />
          </div>
          <div className="full-width">
            <Input
              id="mother_name"
              label="Nome da mãe"
              value={formData.mother_name}
              onChange={handleChange}
              icon={FiUser}
            />
          </div>
          <Input
            id="height"
            label="Altura (m)"
            type="number"
            value={formData.height}
            onChange={handleChange}
            placeholder="Ex: 1,75"
            icon={FiUserCheck}
          />
          <Input
            id="weight"
            label="Peso (kg)"
            type="number"
            value={formData.weight}
            onChange={handleChange}
            placeholder="Ex: 80"
            icon={FiUserCheck}
          />
          <Select
            id="is_pcd"
            label="PCD"
            value={formData.is_pcd}
            onChange={handleChange}
            options={[
              { value: 'false', label: 'Não' },
              { value: 'true', label: 'Sim' },
            ]}
            icon={FiUserCheck}
            searchable
          />
          {formData.is_pcd === 'true' && (
            <Select
              id="disability_type"
              label="Tipo de deficiência"
              value={formData.disability_type}
              onChange={handleChange}
              options={disabilityTypeOptions}
              icon={FiFileText}
              searchable
            />
          )}
        </div>

        <h3 className="section-title">Dados bancários</h3>
        <div className="form-grid">
          <Select
            id="has_pix"
            label="Possui PIX"
            value={formData.has_pix}
            onChange={handleChange}
            options={[
              { value: 'false', label: 'Não' },
              { value: 'true', label: 'Sim' },
            ]}
            icon={FiZap}
            searchable
          />
          {formData.has_pix === 'true' && (
            <>
              <Select
                id="pix_key_type"
                label="Tipo de chave PIX"
                value={formData.pix_key_type}
                onChange={handleChange}
                options={[
                  { value: '', label: 'SELECIONE...' },
                  { value: 'cpf', label: 'CPF' },
                  { value: 'celular', label: 'Celular' },
                  { value: 'email', label: 'E-mail' },
                  { value: 'cnpj', label: 'CNPJ' },
                  { value: 'chave_aleatoria', label: 'Chave aleatória' },
                ]}
                icon={FiType}
                searchable
              />
              <Input
                id="pix_key"
                label="Chave PIX"
                value={formData.pix_key}
                onChange={handleChange}
                mask={getPixKeyMask(formData.pix_key_type)}
                icon={FiKey}
              />
            </>
          )}
          {formData.has_pix === 'false' && (
            <>
              <Select
                id="bank_name"
                label="Banco"
                value={formData.bank_name}
                onChange={handleChange}
                options={bankOptions}
                icon={FiCreditCard}
                searchable
              />
              <Input
                id="bank_agency"
                label="Agência"
                value={formData.bank_agency}
                onChange={handleChange}
                mask="agency"
                icon={FiMapPin}
              />
              <Input
                id="bank_account"
                label="Conta"
                value={formData.bank_account}
                onChange={handleChange}
                mask="account"
                icon={FiHash}
              />
              <Select
                id="bank_account_type"
                label="Tipo de conta"
                value={formData.bank_account_type}
                onChange={handleChange}
                options={[
                  { value: '', label: 'Selecione o tipo de conta' },
                  { value: 'Corrente', label: 'Corrente' },
                  { value: 'Poupança', label: 'Poupança' },
                  { value: 'Salário', label: 'Salário' },
                  { value: 'Pagamento', label: 'Pagamento' },
                  { value: 'Outros', label: 'Outros' },
                ]}
                icon={FiList}
                searchable
              />
            </>
          )}
        </div>

        <h3 className="section-title">Endereço</h3>
        <div className="form-grid">
          <Input
            id="address_zip_code"
            label="CEP"
            value={formData.address_zip_code}
            onChange={handleChange}
            mask="cep"
            onBlur={handleCepBlur}
            placeholder="00000-000"
            icon={FiMapPin}
          />
          <Input
            id="address_street"
            label="Rua / Avenida"
            value={formData.address_street}
            onChange={handleChange}
            icon={FiMapPin}
          />
          <Input
            id="address_number"
            label="Número"
            value={formData.address_number}
            onChange={handleChange}
            icon={FiHome}
          />
          <Input
            id="address_complement"
            label="Complemento"
            value={formData.address_complement}
            onChange={handleChange}
            placeholder="Ex: Ap 101"
            icon={FiHome}
          />
          <Input
            id="address_district"
            label="Bairro"
            value={formData.address_district}
            onChange={handleChange}
            icon={FiMapPin}
          />
          <Input
            id="address_city"
            label="Cidade"
            value={formData.address_city}
            onChange={handleChange}
            icon={FiMapPin}
          />
          <Select
            id="address_state"
            label="Estado (UF)"
            value={formData.address_state || ''}
            onChange={handleChange}
            icon={FiFlag}
            searchable
            options={[
              { value: '', label: 'Selecione o estado (UF)' },
              { value: 'AC', label: 'AC - Acre' },
              { value: 'AL', label: 'AL - Alagoas' },
              { value: 'AP', label: 'AP - Amapá' },
              { value: 'AM', label: 'AM - Amazonas' },
              { value: 'BA', label: 'BA - Bahia' },
              { value: 'CE', label: 'CE - Ceará' },
              { value: 'DF', label: 'DF - Distrito Federal' },
              { value: 'ES', label: 'ES - Espírito Santo' },
              { value: 'GO', label: 'GO - Goiás' },
              { value: 'MA', label: 'MA - Maranhão' },
              { value: 'MT', label: 'MT - Mato Grosso' },
              { value: 'MS', label: 'MS - Mato Grosso do Sul' },
              { value: 'MG', label: 'MG - Minas Gerais' },
              { value: 'PA', label: 'PA - Pará' },
              { value: 'PB', label: 'PB - Paraíba' },
              { value: 'PR', label: 'PR - Paraná' },
              { value: 'PE', label: 'PE - Pernambuco' },
              { value: 'PI', label: 'PI - Piauí' },
              { value: 'RJ', label: 'RJ - Rio de Janeiro' },
              { value: 'RN', label: 'RN - Rio Grande do Norte' },
              { value: 'RS', label: 'RS - Rio Grande do Sul' },
              { value: 'RO', label: 'RO - Rondônia' },
              { value: 'RR', label: 'RR - Roraima' },
              { value: 'SC', label: 'SC - Santa Catarina' },
              { value: 'SP', label: 'SP - São Paulo' },
              { value: 'SE', label: 'SE - Sergipe' },
              { value: 'TO', label: 'TO - Tocantins' },
            ]}
          />
        </div>

        {!isEdit && (
          <>
            <h3 className="section-title">Despesas Recorrentes</h3>
            <div className="form-grid">
              <Select
                id="create_recurring"
                label="Desejo criar despesas salariais recorrentes"
                value={createRecurringExpense}
                onChange={(e) => setCreateRecurringExpense(e.target.value)}
                options={[
                  { value: 'false', label: 'Não' },
                  { value: 'true', label: 'Sim' },
                ]}
                icon={FiRepeat}
                searchable
              />

              {createRecurringExpense === 'true' && (
                <>
                  <div style={{ 
                    gridColumn: '1 / -1', 
                    backgroundColor: '#e3f2fd', 
                    color: '#0d47a1', 
                    padding: '12px', 
                    borderRadius: '8px', 
                    marginBottom: '8px',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    border: '1px solid #bbdefb'
                  }}>
                    <FiInfo size={24} style={{ flexShrink: 0 }} />
                    <span>
                      <strong>Nota:</strong> Os lançamentos gerados servem como previsão de despesas. Os valores podem sofrer ajustes após a apuração da folha de ponto mensal.
                    </span>
                  </div>

                  <Select
                    id="expenseType"
                    label="Tipo de Despesa"
                    value={expenseType}
                    onChange={(e) => setExpenseType(e.target.value)}
                    options={[
                      { value: 'salary', label: 'Incluir apenas salário' },
                      /*{ value: 'salary_benefits', label: 'Incluir salário + benefícios' },*/
                    ]}
                    icon={FiDollarSign}
                    searchable
                  />

                  <Select
                    id="repetition"
                    label="Repetição *"
                    value={recurrenceData.repetition}
                    onChange={handleRecurrenceChange}
                    options={[
                      { value: 'MENSAL', label: 'MENSAL' },
                      { value: 'SEMANAL', label: 'SEMANAL' },
                      { value: 'QUINZENAL', label: 'QUINZENAL' },
                      { value: 'ANUAL', label: 'ANUAL' },
                    ]}
                    icon={FiRepeat}
                    searchable
                  />

                  <Select
                    id="duration"
                    label="Duração *"
                    value={recurrenceData.duration}
                    onChange={handleRecurrenceChange}
                    options={[
                      { value: '12', label: '1 Ano' },
                      { value: '24', label: '2 Anos' },
                      { value: '36', label: '3 Anos' },
                      { value: '48', label: '4 Anos' },
                      { value: '60', label: '5 Anos' },
                    ]}
                    icon={FiClock}
                    searchable
                  />

                  <Select
                    id="charge_on"
                    label="Cobrar sempre no *"
                    value={recurrenceData.charge_on}
                    onChange={handleRecurrenceChange}
                    options={[
                       ...Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: `${i + 1}º DIA DO MÊS` }))
                    ]}
                    icon={FiCalendar}
                    searchable
                  />

                  <Input
                    id="first_due_date"
                    label="1º vencimento *"
                    type="date"
                    value={recurrenceData.first_due_date}
                    onChange={handleRecurrenceChange}
                    icon={FiCalendar}
                  />

                  <Select
                    id="payment_method"
                    label="Forma de pagamento"
                    value={recurrenceData.payment_method}
                    onChange={handleRecurrenceChange}
                    options={paymentMethodOptions}
                    icon={FiCreditCard}
                    searchable
                  />

                  <Select
                    id="category_id"
                    label="Categoria *"
                    value={recurrenceData.category_id}
                    onChange={handleRecurrenceChange}
                    options={categoryOptions}
                    icon={FiTag}
                    searchable
                  />

                  <Select
                    id="cost_center_id"
                    label="Centro de custo *"
                    value={recurrenceData.cost_center_id}
                    onChange={handleRecurrenceChange}
                    options={costCenterOptions}
                    icon={FiLayers}
                    searchable
                  />
                </>
              )}
            </div>
          </>
        )}

        <h3 className="section-title">Configuração de Ronda de Vigilância</h3>
        <div className="form-grid">
          <Select
            id="allow_round"
            label="Permite ronda de vigilância?"
            value={formData.allow_round}
            onChange={handleChange}
            options={[
              { value: '', label: 'SELECIONE...' },
              { value: 'false', label: 'Não' },
              { value: 'true', label: 'Sim' },
            ]}
            icon={FiRepeat}
            searchable
          />
        </div>

        {error && <div className="form-error">{error}</div>}
      </div>

      <div className="form-actions">
        <Button
          type="button"
          className="cancel-button"
          onClick={onCancel}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Salvando...' : isEdit ? 'Salvar' : 'Salvar'}
        </Button>
      </div>
    </form>
    <Modal
      isOpen={isPositionModalOpen}
      onClose={() => setIsPositionModalOpen(false)}
      title="Novo Cargo"
      size="md"
    >
      <PositionForm
        companyId={selectedCompanyId}
        companies={companies}
        onCancel={() => setIsPositionModalOpen(false)}
        onSuccess={async (created) => {
          try {
            const positionsData = await Api.getPositions(selectedCompanyId);
            if (Array.isArray(positionsData)) {
              setPositions(positionsData);
            }
            if (created && created.id) {
              setFormData(prev => ({ ...prev, position_id: String(created.id) }));
            }
          } catch (err) {
            toast.error('Erro ao atualizar lista de cargos.');
          } finally {
            setIsPositionModalOpen(false);
          }
        }}
      />
    </Modal>
    <Modal
      isOpen={isBenefitModalOpen}
      onClose={() => setIsBenefitModalOpen(false)}
      title="Novo Benefício"
      size="md"
    >
      <BenefitForm
        companyId={selectedCompanyId}
        companies={companies}
        onCancel={() => setIsBenefitModalOpen(false)}
        onSuccess={async (created) => {
          try {
            const benefitsData = await Api.getBenefits(selectedCompanyId);
            if (Array.isArray(benefitsData)) {
              setBenefits(benefitsData);
            }
            if (created && created.id) {
              setSelectedBenefitIds(prev => {
                const id = String(created.id);
                if (prev.includes(id)) return prev;
                return [...prev, id];
              });
            }
          } catch (err) {
            toast.error('Erro ao atualizar lista de benefícios.');
          } finally {
            setIsBenefitModalOpen(false);
          }
        }}
      />
    </Modal>
    </>
  );
}
